import { storableError } from '../../util/errors';
import { skillFitReport } from '../../util/api';

/**
 * `SkillFitReportPage` state — the report a reviewer reads at
 * `/skillfit-report/:candidateListingId/:jobListingId`.
 *
 * ⛔ **No new server contract.** The page reproduces the imperative fetch the Job Applications card
 * used to make: the SAME `GET /api/skillFitReport` call, with the SAME two parameters. There is no
 * report id — the endpoint resolves the document from (employer org, candidate, job), and its
 * access-control rule is written in exactly those terms.
 *
 * ⚠ The two listing ids are load-bearing TOGETHER: a SkillFit Check belongs to a job application,
 * not to a person. Dropping `jobListingId` serves the candidate's first application's
 * report for their second, graded against the other job's rubric.
 */

// The two phases a refusal may name for a finished interview whose report is not written yet.
export const AWAITING_REPORT_PHASES = ['grading', 'delayed'];

// ================ Action types ================ //

export const FETCH_REPORT_REQUEST = 'app/SkillFitReportPage/FETCH_REPORT_REQUEST';
export const FETCH_REPORT_SUCCESS = 'app/SkillFitReportPage/FETCH_REPORT_SUCCESS';
export const FETCH_REPORT_ERROR = 'app/SkillFitReportPage/FETCH_REPORT_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  report: null,
};

export default function skillFitReportPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_REPORT_REQUEST:
      // ⛔ The previous report is dropped on the way IN, not on the way out. Two reviewers' reports
      // are reached by two URLs on the same page, so a request that leaves the old document standing
      // would show one candidate's evidence under another candidate's address while it loads.
      return { ...state, fetchInProgress: true, fetchError: null, report: null };
    case FETCH_REPORT_SUCCESS:
      return { ...state, fetchInProgress: false, report: payload };
    case FETCH_REPORT_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchReportRequest = () => ({ type: FETCH_REPORT_REQUEST });
export const fetchReportSuccess = payload => ({ type: FETCH_REPORT_SUCCESS, payload });
export const fetchReportError = e => ({ type: FETCH_REPORT_ERROR, error: true, payload: e });

// ================ Thunks ================ //

/**
 * ⛔ **Resolves even when the fetch fails, and never rethrows.** The refusals this endpoint issues
 * are the page's content, not a broken load: `404` is "not generated yet" and `403` is the org gate
 * declining to disclose that a check exists at all. Rejecting here would fail the data-loading pass
 * and hand the reviewer a blank document instead of the server's answer.
 *
 * `storableError` keeps `status`, which is the only thing that separates those two states from a
 * genuine failure — see the page's `resolveViewState`.
 *
 * ⚠ It keeps no field of the refusal's BODY, and a `404` may carry one: `awaitingReport`, saying the
 * interview has finished and its report is still to come. It is carried across here by hand, and
 * only when it is a phase the page knows how to say — anything else is stored as `null`, so an
 * unexpected value reads as "not available" rather than as a state nobody wrote words for.
 */
export const loadData = params => dispatch => {
  const candidateListingId = params && params.candidateListingId;
  const jobListingId = params && params.jobListingId;

  dispatch(fetchReportRequest());
  return skillFitReport(candidateListingId, jobListingId)
    .then(response => {
      dispatch(fetchReportSuccess(response));
      return response;
    })
    .catch(e => {
      const phase = e && e.awaitingReport;
      const awaitingReport = AWAITING_REPORT_PHASES.includes(phase) ? phase : null;
      dispatch(fetchReportError({ ...storableError(e), awaitingReport }));
    });
};
